/**
 * API Settings Page
 *
 * Manage public API keys (read key + write key).
 * Owner-only - controls API access for the workspace.
 */

import { Check, Copy, KeyRound, Plus, RotateCw, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@loader/ui';

import { SettingsPageLayout } from '../components';

import { usePublicApiKey } from '@/features/links/hooks/usePublicApiKey';
import { useCopyToClipboard, useWorkspaceMembership } from '@/hooks';
import { useToastStore } from '@/store/useToasts';

// ============================================================================
// KEY CARD (shared for read and write)
// ============================================================================

function KeyCard({
  title,
  description,
  apiKey,
  isRegenerating,
  onRegenerate,
  rotateLabel,
  rotateConfirmTitle,
  rotateConfirmDescription,
  actions,
}: {
  title: string;
  description: string;
  apiKey: string | null;
  isRegenerating: boolean;
  onRegenerate: () => void;
  rotateLabel: string;
  rotateConfirmTitle: string;
  rotateConfirmDescription: string;
  actions?: React.ReactNode;
}) {
  const { copy, isCopied } = useCopyToClipboard({ successMessage: `${title} copied` });

  const maskedKey = apiKey
    ? `${apiKey.slice(0, 8)}${'•'.repeat(24)}${apiKey.slice(-4)}`
    : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {maskedKey ? (
          <div className="flex items-center gap-3">
            <code className="bg-muted flex-1 rounded-md px-3 py-2 font-mono text-sm">
              {maskedKey}
            </code>
            <Button
              variant="outline"
              size="icon"
              onClick={() => apiKey && copy(apiKey, 'key')}
              title={`Copy ${title.toLowerCase()}`}
            >
              {isCopied('key') ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="icon" title={rotateLabel}>
                  <RotateCw className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{rotateConfirmTitle}</AlertDialogTitle>
                  <AlertDialogDescription>{rotateConfirmDescription}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onRegenerate} disabled={isRegenerating}>
                    {isRegenerating ? 'Rotating...' : 'Rotate key'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {actions}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No key found.</p>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// WRITE KEY SECTION (card or generate button)
// ============================================================================

function WriteKeySection({
  writeKey,
  isRegenerating,
  onRegenerate,
  isCreating,
  onCreate,
  isDeleting,
  onDelete,
}: {
  writeKey: string | null;
  isRegenerating: boolean;
  onRegenerate: () => void;
  isCreating: boolean;
  onCreate: () => void;
  isDeleting: boolean;
  onDelete: () => void;
}) {
  if (!writeKey) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Write Key
          </CardTitle>
          <CardDescription>
            Generate a write key to allow POST requests for inserting data into tables via the API
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={onCreate} disabled={isCreating} variant="outline">
            <Plus className="mr-2 h-4 w-4" />
            {isCreating ? 'Generating...' : 'Generate Write Key'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <KeyCard
      title="Write Key"
      description="Used for POST requests to insert data into tables"
      apiKey={writeKey}
      isRegenerating={isRegenerating}
      onRegenerate={onRegenerate}
      rotateLabel="Rotate write key"
      rotateConfirmTitle="Rotate write key?"
      rotateConfirmDescription="This will invalidate the current write key and generate a new one. Any integrations using the current write key will stop working immediately."
      actions={
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="icon" title="Delete write key">
              <Trash2 className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete write key?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the write key. POST requests to insert data will stop
                working. You can generate a new write key later.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete} disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Delete write key'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      }
    />
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export function ApiSettingsPage() {
  const { isOwner, isLoading: isMembershipLoading } = useWorkspaceMembership();
  const {
    readKey,
    writeKey,
    writeKeyId,
    loading,
    regenerateReadKey,
    regeneratingRead,
    regenerateWriteKey,
    regeneratingWrite,
    createWriteKey,
    creatingWriteKey,
    deleteWriteKey,
    deletingWriteKey,
  } = usePublicApiKey();
  const { success: showSuccess, error: showError } = useToastStore();

  const handleRegenerateReadKey = () => {
    regenerateReadKey(undefined, {
      onSuccess: () => showSuccess('Read key rotated successfully'),
      onError: () => showError('Failed to rotate read key'),
    });
  };

  const handleRegenerateWriteKey = () => {
    regenerateWriteKey(undefined, {
      onSuccess: () => showSuccess('Write key rotated successfully'),
      onError: () => showError('Failed to rotate write key'),
    });
  };

  const handleCreateWriteKey = () => {
    createWriteKey(undefined, {
      onSuccess: () => showSuccess('Write key generated'),
      onError: () => showError('Failed to generate write key'),
    });
  };

  const handleDeleteWriteKey = () => {
    if (!writeKeyId) return;
    deleteWriteKey(writeKeyId, {
      onSuccess: () => showSuccess('Write key deleted'),
      onError: () => showError('Failed to delete write key'),
    });
  };

  return (
    <SettingsPageLayout
      title="API"
      description="Manage your workspace's public API keys and access settings"
      isLoading={loading || isMembershipLoading}
      isOwner={isOwner}
      requiresOwner
    >
      <KeyCard
        title="Read Key"
        description="Used for GET requests to read data from tables and linked tables"
        apiKey={readKey}
        isRegenerating={regeneratingRead}
        onRegenerate={handleRegenerateReadKey}
        rotateLabel="Rotate read key"
        rotateConfirmTitle="Rotate read key?"
        rotateConfirmDescription="This will invalidate the current read key and generate a new one. Any integrations using the current read key (public links, OData, Power BI) will stop working immediately."
      />

      <WriteKeySection
        writeKey={writeKey}
        isRegenerating={regeneratingWrite}
        onRegenerate={handleRegenerateWriteKey}
        isCreating={creatingWriteKey}
        onCreate={handleCreateWriteKey}
        isDeleting={deletingWriteKey}
        onDelete={handleDeleteWriteKey}
      />
    </SettingsPageLayout>
  );
}
